import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api.js'

const AuthContext = createContext(null)

/**
 * Session state for the whole app.
 *
 * The bootstrap call is `GET /api/v1/auth/session`, which answers 200 whether
 * or not anyone is signed in and issues the CSRF cookie on the way. Until it
 * returns, `status` is 'loading' and routes render nothing — otherwise every
 * reload of an authenticated page flashes the sign-in screen first.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [status, setStatus] = useState('loading')
  // Whether the person signed themselves out, as opposed to arriving signed
  // out or having a session expire. The guard reads it so a sign-out does not
  // hand the page it left to whoever signs in next (Q8).
  const [signedOut, setSignedOut] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const data = await api.session()
      setUser(data.authenticated ? data.user : null)
      setStatus('ready')
      return data.authenticated ? data.user : null
    } catch {
      // A failed bootstrap means the API is unreachable, not that the person
      // is signed out. The shell shows an error state rather than /login.
      setUser(null)
      setStatus('error')
      return null
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const value = useMemo(
    () => ({
      user,
      status,
      signedOut,
      isAuthenticated: Boolean(user),
      refresh,
      async login(credentials) {
        const me = await api.login(credentials)
        setUser(me)
        setStatus('ready')
        setSignedOut(false)
        return me
      },
      // The second step of a login for an enrolled account (1B-4a). Separate
      // from `login` because `login` must never be handed a problem body:
      // `totp-required` is an ApiError the page catches, not a user object.
      async loginWithCode(body) {
        const me = await api.loginWithCode(body)
        setUser(me)
        setStatus('ready')
        setSignedOut(false)
        return me
      },
      async loginWithRecoveryCode(body) {
        const me = await api.loginWithRecoveryCode(body)
        setUser(me)
        setStatus('ready')
        setSignedOut(false)
        return me
      },
      async signup(body) {
        const me = await api.signup(body)
        setUser(me)
        setStatus('ready')
        setSignedOut(false)
        return me
      },
      async logout() {
        try {
          await api.logout()
        } finally {
          setSignedOut(true)
          setUser(null)
        }
      },
      async updateMe(changes) {
        const me = await api.updateMe(changes)
        setUser(me)
        return me
      },
      async setAvatar(file) {
        const me = await api.setAvatar(file)
        setUser(me)
        return me
      },
      async removeAvatar() {
        const me = await api.removeAvatar()
        setUser(me)
        return me
      },
    }),
    [user, status, signedOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
